// Kleuren per GameOn-hoofdcategorie (type × energie), volgens AGENTS.md §2:
// semi-transparante tonale kaarten (mesh schijnt door), borderloos, tekst uit dezelfde kleurfamilie.
// Gedeeld door Ideeën, Kaart en Game Over — houd deze vorm stabiel.
import { Armchair, Bike, Puzzle, TreePine, type LucideIcon } from 'lucide-react';
import type { MainCategory } from '../domain/types';

export interface MainCategoryTheme {
  /** Korte Nederlandse naam, bijv. "Buiten · Veel energie". */
  label: string;
  /** Semantisch icoon (lucide) voor badges. */
  icon: LucideIcon;
  /** Kaartvlak licht + donker (incl. `dark:`). */
  cardBg: string;
  /** Hoofdtekst licht + donker. */
  textColor: string;
  /** Accent-/secundaire tekst licht + donker. */
  accent: string;
  /** Pillen/badges licht + donker. */
  pillBg: string;
  /** Intern vlak (bijv. timer- of statistiekblok) licht + donker. */
  timerBoxBg: string;
  /** Effen kleur voor stippen/markers/grafieken (hex, werkt in beide modi). */
  hex: string;
  /** Volle knop-achtergrond (primaire actie op de kaart) licht + donker. */
  buttonBg: string;
}

export const MAIN_CATEGORY_THEMES: Record<MainCategory, MainCategoryTheme> = {
  'buiten-hoog': {
    label: 'Buiten · Veel energie',
    icon: Bike,
    cardBg: 'bg-emerald-100/90 dark:bg-emerald-950/70',
    textColor: 'text-emerald-950 dark:text-emerald-50',
    accent: 'text-emerald-800 dark:text-emerald-300',
    pillBg: 'bg-emerald-200/80 dark:bg-emerald-900/60',
    timerBoxBg: 'bg-emerald-200/50 dark:bg-emerald-900/40',
    hex: '#059669',
    buttonBg: 'bg-emerald-800 text-emerald-50 hover:bg-emerald-900 dark:bg-emerald-300 dark:text-emerald-950 dark:hover:bg-emerald-200',
  },
  'buiten-rustig': {
    label: 'Buiten · Rustig',
    icon: TreePine,
    cardBg: 'bg-amber-100/90 dark:bg-amber-950/70',
    textColor: 'text-amber-950 dark:text-amber-50',
    accent: 'text-amber-800 dark:text-amber-300',
    pillBg: 'bg-amber-200/80 dark:bg-amber-900/60',
    timerBoxBg: 'bg-amber-200/50 dark:bg-amber-900/40',
    hex: '#d97706',
    buttonBg: 'bg-amber-800 text-amber-50 hover:bg-amber-900 dark:bg-amber-300 dark:text-amber-950 dark:hover:bg-amber-200',
  },
  'binnen-hoog': {
    label: 'Binnen · Veel energie',
    icon: Puzzle,
    cardBg: 'bg-rose-100/90 dark:bg-rose-950/70',
    textColor: 'text-rose-950 dark:text-rose-50',
    accent: 'text-rose-800 dark:text-rose-300',
    pillBg: 'bg-rose-200/80 dark:bg-rose-900/60',
    timerBoxBg: 'bg-rose-200/50 dark:bg-rose-900/40',
    hex: '#e11d48',
    buttonBg: 'bg-rose-800 text-rose-50 hover:bg-rose-900 dark:bg-rose-300 dark:text-rose-950 dark:hover:bg-rose-200',
  },
  'binnen-rustig': {
    label: 'Binnen · Rustig',
    icon: Armchair,
    cardBg: 'bg-sky-100/90 dark:bg-sky-950/70',
    textColor: 'text-sky-950 dark:text-sky-50',
    accent: 'text-sky-800 dark:text-sky-300',
    pillBg: 'bg-sky-200/80 dark:bg-sky-900/60',
    timerBoxBg: 'bg-sky-200/50 dark:bg-sky-900/40',
    hex: '#0284c7',
    buttonBg: 'bg-sky-800 text-sky-50 hover:bg-sky-900 dark:bg-sky-300 dark:text-sky-950 dark:hover:bg-sky-200',
  },
};

export const MAIN_CATEGORY_ORDER: readonly MainCategory[] = ['buiten-hoog', 'buiten-rustig', 'binnen-hoog', 'binnen-rustig'];
